import type { ChannelInput } from '../_shared/channel-types'

/**
 * BlockSpec shape sent to cogo-cloudflare /api/v3/block/generate.
 * Mirrors `apps/cogo-cloudflare/src/workflows/appGenerationWorkflow.ts:81` —
 * we only declare the fields Channel 2 produces; the route accepts a wider
 * type but tolerates the missing optional fields (specV3, pageContext).
 */
export interface BlockSpec {
  name: string
  type: string
  description: string
  binds: string[]
}

/**
 * Translate `ChannelInput.intent` into a backend `BlockSpec`.
 *
 * Phase 1 minimal mapping: the user's `pattern` text becomes the block
 * description; `name` is a kebab-case slug; `type` is `semanticType` or a
 * generic fallback. The hybrid pipeline's LLM expands description → real
 * block contents.
 *
 * Throws if input is not `kind: 'intent'` — `examplePick` mode has no path
 * to the real backend (Channel 2 already declines it as `unsupported-mode`).
 */
export function intentToBlockSpec(
  input: ChannelInput,
): BlockSpec {
  if (input.kind !== 'intent') {
    throw new Error(
      `intentToBlockSpec: only 'intent' input is supported, got '${input.kind}'`,
    )
  }
  const description = (input.pattern ?? input.semanticType ?? '').trim() || 'untitled block'
  const slugSource = input.pattern ?? input.semanticType ?? 'block'
  const slug =
    slugSource
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'block'
  return {
    name: slug,
    type: input.semanticType ?? 'section',
    description,
    binds: [],
  }
}
